import {
  BALANCE,
  MARKET,
  TRADE_GOOD_IDS,
  type SlotLayer,
  type TechId,
  type TradeGoodId,
} from '@sd/content';
import { t } from '@sd/i18n';
import type { Command, GameState } from '@sd/sim';
import type { Viewer } from '../map/describe';
import { actionCounts, leverReady, tabCount, type ActionCounts } from './actionCounts';
import { escapeHtml, num, syllables } from './format';
import { icon, type IconName } from './icons';
import {
  ORDER_SIZES,
  deal,
  dumpingImpact,
  marketView,
  proposal,
  type MarketView,
} from './marketModel';
import { diplomacyView } from './diplomacyModel';
import { fleetView, type FleetView } from './fleetModel';
import { planetView, slotRemedy, type Offer, type PlanetView } from './planetModel';
import { researchView } from './researchModel';
import { progressItems } from '../map/progress';
import {
  canDot,
  diplomacyTab,
  fleetPinned,
  fleetPop,
  fleetShips,
  fleetYard,
  pinRow,
  researchPinned,
  researchPop,
  researchTab,
  subTabs,
  type PopCard,
} from './sideTabs';

/**
 * The right-hand panel (#57, #58, docs/design/ui-style.md): tabs with the
 * player's capital, the exchange, research, fleets and diplomacy. It folds
 * into a rail of icons by the handle on its edge or the `]` key; F1–F5
 * switch tabs. Buttons carry ready commands; the panel is
 * redrawn only when its words change, and never under a field the player
 * is typing into.
 */
export type TabId = 'planet' | 'market' | 'research' | 'fleet' | 'diplomacy';
const TABS: readonly { id: TabId; icon: IconName; key: string }[] = [
  { id: 'planet', icon: 'planet', key: 'F1' },
  { id: 'market', icon: 'market', key: 'F2' },
  { id: 'research', icon: 'research', key: 'F3' },
  { id: 'fleet', icon: 'ship', key: 'F4' },
  { id: 'diplomacy', icon: 'players', key: 'F5' },
];
type MarketSub = 'prices' | 'currency' | 'levers' | 'contracts' | 'auto';
const MARKET_SUBS: readonly MarketSub[] = ['prices', 'currency', 'levers', 'contracts', 'auto'];
type FleetSub = 'yard' | 'ships';
/**
 * The card open beside the panel (#152): what to build in a free slot, a building to
 * upgrade, a technology, a ship class.
 */
type Pop =
  | { readonly kind: 'slot'; readonly layer: SlotLayer }
  | { readonly kind: 'building'; readonly index: number }
  | { readonly kind: 'tech'; readonly id: TechId }
  | { readonly kind: 'ship'; readonly id: string };
/** What can be done is counted at most this often, in ticks: it builds every tab's view. */
const COUNT_TICKS = 10;
/** Closed cycles of price history kept for the sparklines. */
const HISTORY = 20;
/** The panel redraws at most this often between the player's own actions. */
const REDRAW_MS = 250;

const goodIcon = (g: TradeGoodId) => `<i class="sd-good-icon" data-good="${g}"></i>`;
const cmd = (c: Command) => escapeHtml(JSON.stringify(c));

export class SidePanel {
  private readonly el: HTMLElement;
  private readonly handle: HTMLButtonElement;
  private readonly body: HTMLElement;
  private open = true;
  private tab: TabId = 'planet';
  private last = '';
  private state: GameState | null = null;
  private readonly history: Partial<Record<TradeGoodId, number[]>> = {};
  private lastCycle = -1;
  /** The good picked in the price table. */
  private good: TradeGoodId = 'food';
  private marketSub: MarketSub = 'prices';
  /** The yard first (#152, the author's choice): building ships is what the tab is opened for. */
  private fleetSub: FleetSub = 'yard';
  private pop: Pop | null = null;
  /** Where the card stands, from the top of the panel: beside what opened it. */
  private popTop = 0;
  private counts: ActionCounts | null = null;
  /** The player's fleets hit lately (#156): a red mark on their rows. */
  private hit: ReadonlySet<number> = new Set();
  private countsTick = -Infinity;
  /** A pointer is down on the panel: a redraw now would swallow the click. */
  private pressed = false;
  private lastDraw = 0;
  private form = {
    other: '',
    role: 'sell' as 'sell' | 'buy',
    good: 'food' as TradeGoodId,
    quantity: 20,
    price: 0,
    deliveries: 5,
  };

  constructor(
    parent: HTMLElement,
    private readonly viewer: Viewer,
    private readonly order: (command: Command) => void,
    private readonly onFold: (open: boolean) => void,
    /** Select a fleet on the map and fly the camera to it. */
    private readonly onFocusFleet: (fleetId: number) => void = () => {},
  ) {
    this.el = document.createElement('aside');
    this.el.className = 'sd-side';
    this.handle = document.createElement('button');
    this.handle.type = 'button';
    this.handle.className = 'sd-toggle sd-toggle--right';
    this.body = document.createElement('div');
    this.body.className = 'sd-side__body';
    this.el.append(this.handle, this.body);
    parent.append(this.el);
    this.drawHandle();

    this.el.addEventListener('pointerdown', () => (this.pressed = true));
    window.addEventListener('pointerup', () => (this.pressed = false));
    this.el.addEventListener('click', (event) => this.click(event));
    this.el.addEventListener('change', (event) => this.change(event));
  }

  get isOpen(): boolean {
    return this.open;
  }

  setHit(ids: ReadonlySet<number>) {
    if (ids.size === this.hit.size && [...ids].every((id) => this.hit.has(id))) return;
    this.hit = ids;
    if (this.tab === 'fleet') this.redraw(true);
  }

  toggle() {
    this.open = !this.open;
    this.pop = null;
    this.drawHandle();
    this.redraw(true);
    this.onFold(this.open);
  }

  /** `]` folds, F1–F5 open a tab; true when the key was used. */
  handleKey(event: KeyboardEvent): boolean {
    if (event.code === 'Escape' && this.pop) {
      this.closePop();
      return true;
    }
    if (event.code === 'BracketRight') {
      this.toggle();
      return true;
    }
    const tab = TABS.find((x) => x.key === event.code);
    if (!tab) return false;
    event.preventDefault();
    this.show(tab.id);
    return true;
  }

  show(tab: TabId) {
    if (tab !== this.tab) this.pop = null;
    this.tab = tab;
    if (!this.open) {
      this.open = true;
      this.drawHandle();
      this.onFold(true);
    }
    this.redraw(true);
  }

  update(state: GameState) {
    this.state = state;
    const cycle = Math.floor(state.tick / BALANCE.cycleTicks);
    if (cycle !== this.lastCycle) {
      this.lastCycle = cycle;
      for (const g of TRADE_GOOD_IDS) {
        const list = (this.history[g] ??= []);
        list.push(state.market[g].price);
        if (list.length > HISTORY) list.shift();
      }
    }
    this.redraw(false);
  }

  private drawHandle() {
    const { locale } = this.viewer;
    this.el.dataset.open = String(this.open);
    this.handle.setAttribute('aria-expanded', String(this.open));
    this.handle.title = t(locale, this.open ? 'ui.panel.foldRight' : 'ui.panel.unfoldRight');
    this.handle.innerHTML = icon(this.open ? 'right' : 'left', 16);
  }

  private redraw(force: boolean) {
    const state = this.state;
    if (!state) return;
    // Never redraw under the player's fingers: a field in focus keeps its value.
    const focused = document.activeElement;
    if (!force && focused && this.el.contains(focused) && focused.matches('input, select')) return;
    // Prices move every tick; four redraws a second are plenty, and none mid-click.
    const now = performance.now();
    if (!force && (this.pressed || now - this.lastDraw < REDRAW_MS)) return;
    this.lastDraw = now;
    if (force || Math.abs(state.tick - this.countsTick) >= COUNT_TICKS) {
      this.counts = actionCounts(state, this.viewer, this.history);
      this.countsTick = state.tick;
    }
    const html = this.open ? this.render(state) : this.rail();
    if (!force && html === this.last) return;
    this.last = html;
    const scroll = this.body.querySelector('.sd-side__scroll')?.scrollTop ?? 0;
    this.body.innerHTML = html;
    const pane = this.body.querySelector('.sd-side__scroll');
    if (pane) pane.scrollTop = scroll;
  }

  /** The green count of what can be done in a tab (#152), or nothing. */
  private count(tab: TabId): number {
    return this.counts ? tabCount(this.counts, tab) : 0;
  }

  private tabTitle(tab: { id: TabId; key: string }): string {
    const { locale } = this.viewer;
    const n = this.count(tab.id);
    return `${t(locale, `tab.${tab.id}`)} (${tab.key})${n ? ` · ${t(locale, 'ui.panel.canDo', { n })}` : ''}`;
  }

  private rail(): string {
    return `<nav class="sd-panel sd-rail">${TABS.map((tab) => {
      const n = this.count(tab.id);
      return `<button class="sd-rail__btn" type="button" data-tab="${tab.id}" title="${escapeHtml(this.tabTitle(tab))}">${icon(tab.icon)}${n ? `<i class="sd-count" aria-hidden="true">${n}</i>` : ''}</button>`;
    }).join('')}</nav>`;
  }

  private render(state: GameState): string {
    const { locale } = this.viewer;
    const tabs = `<ul class="sd-tabs" role="tablist">${TABS.map((tab) => {
      const on = tab.id === this.tab;
      const n = this.count(tab.id);
      return `<li><a href="#" role="tab" data-tab="${tab.id}" ${on ? 'aria-current="page"' : ''} title="${escapeHtml(this.tabTitle(tab))}">${icon(tab.icon, 18)}${on ? t(locale, `tab.${tab.id}`) : ''}</a>${n ? `<i class="sd-count" aria-hidden="true">${n}</i>` : ''}</li>`;
    }).join('')}</ul>`;
    const content = this.content(state);
    const pin = this.pinned(state);
    const card = this.popCard(state);
    // What is under way stays on top; the list under it scrolls (#115).
    return `${tabs}<div class="sd-panel sd-side__body">${pin ? `<div class="sd-side__pin">${pin}</div>` : ''}<div class="sd-side__scroll">${content}</div></div>${
      card
        ? `<section class="sd-panel sd-pop" role="dialog" aria-label="${escapeHtml(card.title)}" style="top:${this.popTop}px;max-height:calc(100% - ${this.popTop + 10}px)">
            <header class="sd-pop__head"><b>${escapeHtml(card.title)}</b>${card.sub ? `<span class="sd-label">${escapeHtml(card.sub)}</span>` : ''}<button type="button" class="sd-btn sd-btn--icon sd-btn--small" data-action="close-pop" aria-label="${t(locale, 'ui.notes.close')}">×</button></header>
            ${card.body}
          </section>`
        : ''
    }`;
  }

  /** The card beside the panel, made fresh with every redraw so its prices stay live. */
  private popCard(state: GameState): PopCard | null {
    const pop = this.pop;
    if (!pop) return null;
    const { locale } = this.viewer;
    switch (pop.kind) {
      case 'slot':
      case 'building': {
        const view = planetView(state, this.viewer);
        return view ? this.planetPop(view, pop) : null;
      }
      case 'tech': {
        const view = researchView(state, this.viewer);
        return view ? researchPop(locale, view, pop.id) : null;
      }
      case 'ship': {
        const view = fleetView(state, this.viewer);
        return view ? fleetPop(locale, view, pop.id) : null;
      }
    }
  }

  private closePop() {
    this.pop = null;
    this.redraw(true);
  }

  /** The market's sub-tabs, pinned with any notice over them. */
  private marketTabs = '';

  /** The pinned top of the open tab (#115): what is being built or learnt, and sub-tabs. */
  private pinned(state: GameState): string {
    const { locale } = this.viewer;
    switch (this.tab) {
      case 'planet': {
        const items = progressItems(state, this.viewer.me, locale).filter((i) =>
          i.id.startsWith('build:'),
        );
        return items.length
          ? items.map((i) => pinRow(i.label, i.value, i.left ?? '')).join('')
          : `<p class="sd-label">${t(locale, 'planet.underwayNone')}</p>`;
      }
      case 'market':
        return this.marketTabs;
      case 'research': {
        const view = researchView(state, this.viewer);
        return view ? researchPinned(locale, view) : '';
      }
      case 'fleet': {
        const view = fleetView(state, this.viewer);
        if (!view) return '';
        const c = this.counts?.fleet;
        const tabs = subTabs(
          [
            { id: 'yard', label: t(locale, 'fleet.shipyard'), count: c?.yard ?? 0 },
            { id: 'ships', label: t(locale, 'fleet.ships'), count: c?.ships ?? 0 },
          ],
          this.fleetSub,
        );
        return this.fleetSub === 'yard' ? `${tabs}${fleetPinned(locale, view)}` : tabs;
      }
      default:
        return '';
    }
  }

  private content(state: GameState): string {
    const { locale } = this.viewer;
    switch (this.tab) {
      case 'planet':
        return this.planet(planetView(state, this.viewer));
      case 'market':
        return this.market(state, marketView(state, this.viewer, this.history));
      case 'research': {
        const view = researchView(state, this.viewer);
        return view
          ? researchTab(locale, view, this.pop?.kind === 'tech' ? this.pop.id : null)
          : '';
      }
      case 'fleet': {
        const view = fleetView(state, this.viewer);
        return view ? this.fleet(view) : '';
      }
      case 'diplomacy': {
        const view = diplomacyView(state, this.viewer);
        return view ? diplomacyTab(locale, view) : '';
      }
    }
  }

  private fleet(view: FleetView): string {
    const { locale } = this.viewer;
    return this.fleetSub === 'yard'
      ? fleetYard(locale, view, this.pop?.kind === 'ship' ? this.pop.id : null)
      : fleetShips(locale, view, this.hit);
  }

  // ---------- planet ----------

  private cost(o: Offer): string {
    const { locale } = this.viewer;
    const parts = [`<span>${num(locale, o.cash)}</span>`];
    if (o.alloys) parts.push(`<span>${goodIcon('alloys')}${num(locale, o.alloys)}</span>`);
    if (o.metals) parts.push(`<span>${goodIcon('metals')}${num(locale, o.metals)}</span>`);
    parts.push(`<span class="sd-muted">${t(locale, 'planet.cycles', { n: o.cycles })}</span>`);
    return `<span class="sd-cost">${parts.join('')}</span>`;
  }

  /**
   * The capital on one screen (#152): its numbers, its slots as tiles. An empty slot
   * opens what can be built in it, a building its upgrade, in a card beside the panel.
   */
  private planet(view: PlanetView | null): string {
    if (!view) return '';
    const { locale } = this.viewer;
    const stats = view.stats
      .map(
        (s) =>
          // The note on hover (#152): the numbers fit two lines.
          `<div class="${s.alert ? 'sd-stat--alert' : ''}" title="${escapeHtml(`${s.label}: ${s.value} · ${s.note}`)}"><dt>${escapeHtml(s.label)}</dt><dd>${escapeHtml(s.value)}</dd></div>`,
      )
      .join('');
    const pop = this.pop;
    const tile = (row: PlanetView['buildings'][number]) => {
      const pips = Array.from({ length: row.maxLevel }, (_, i) => {
        const level = i + 1;
        const cls = level <= row.level ? 'on' : row.building?.level === level ? 'next' : '';
        return `<i class="${cls}"></i>`;
      }).join('');
      const picked = pop?.kind === 'building' && pop.index === row.index;
      // Under construction, the tile fills as it goes (#164).
      const fill = row.building ? Math.round(row.building.value * 100) : 0;
      return `<button type="button" class="sd-panel sd-slot ${row.building ? 'sd-slot--building sd-fill' : ''} ${picked ? 'sd-slot--picked' : ''}" ${row.building ? `style="--p:${fill}%"` : ''} data-building="${row.index}" title="${escapeHtml(`${row.name} · ${row.does}`)}">
          <span class="sd-slot__name">${escapeHtml(syllables(row.name))}</span>
          <span class="sd-slot__level">${pips}</span>
        </button>`;
    };
    const layer = (l: SlotLayer) => {
      const free = Math.max(0, view.slots[l].total - view.slots[l].used);
      const can = view.options.some((o) => o.layer === l && o.offer.command);
      const picked = pop?.kind === 'slot' && pop.layer === l;
      const player = this.state?.players.find((p) => p.id === this.viewer.me);
      // No free slot: one tile still stands there, disabled, saying how to get one (#156).
      const none =
        free === 0 && player
          ? `<div class="sd-panel sd-slot sd-slot--none" role="note">${icon('lock', 14)}${escapeHtml(slotRemedy(locale, player, l, true))}</div>`
          : '';
      const empty =
        none ||
        Array.from(
          { length: free },
          (_, i) =>
            `<button type="button" class="sd-panel sd-slot sd-slot--empty ${picked && i === 0 ? 'sd-slot--picked' : ''}" data-slot="${l}" aria-label="${t(locale, 'planet.emptySlot')}" title="${t(locale, 'planet.emptySlot')}">${can ? canDot : ''}+</button>`,
        ).join('');
      return `<div class="sd-section__head sd-layer"><h3>${t(locale, `planet.layer.${l}`)}</h3><span class="sd-label">${view.slots[l].used}/${view.slots[l].total}</span></div>
       <div class="sd-slots">${view.buildings
         .filter((b) => b.layer === l)
         .map(tile)
         .join('')}${empty}</div>`;
    };
    // Goods the capital neither makes nor uses stay out of the table.
    const moved = view.cycle.filter((r) => r.produced > 0.05 || r.consumed > 0.05);
    const cycle = moved.length
      ? `<table class="sd-table"><thead><tr><th>${t(locale, 'planet.cycle.good')}</th><th>${t(locale, 'planet.cycle.produced')}</th><th>${t(locale, 'planet.cycle.consumed')}</th><th>${t(locale, 'planet.cycle.net')}</th></tr></thead><tbody>${moved
          .map((r) => {
            const cls = r.net > 0.05 ? 'sd-up' : r.net < -0.05 ? 'sd-down' : 'sd-muted';
            return `<tr><td><span class="sd-good">${goodIcon(r.good)}${t(locale, `resource.${r.good}`)}</span></td><td>${num(locale, r.produced, 1)}</td><td>${num(locale, r.consumed, 1)}</td><td class="${cls}">${r.net > 0 ? '+' : ''}${num(locale, r.net, 1)}</td></tr>`;
          })
          .join('')}</tbody></table>`
      : `<p class="sd-label">${t(locale, 'planet.cycle.none')}</p>`;
    return `
      <section class="sd-section">
        <div class="sd-section__head"><h2>${escapeHtml(view.title)}</h2><span class="sd-label">${escapeHtml(view.subtitle)}</span></div>
        <dl class="sd-stats">${stats}</dl>
      </section>
      <section class="sd-section">
        ${layer('surface')}${layer('orbit')}
        <p class="sd-label sd-hint">${t(locale, 'planet.slotHint')}</p>
      </section>
      <section class="sd-section">
        <div class="sd-section__head"><h2>${t(locale, 'planet.cycle')}</h2></div>
        ${cycle}
      </section>`;
  }

  /** What can go into a free slot, or what a building does and its upgrade. */
  private planetPop(view: PlanetView, pop: Pop): PopCard | null {
    const { locale } = this.viewer;
    if (pop.kind === 'slot') {
      const options = view.options
        .filter((o) => o.layer === pop.layer)
        .map(
          (o) => `<li class="sd-option ${o.locked ? 'sd-option--locked' : ''}">
            <div class="sd-option__text">${o.offer.command ? canDot : ''}<b>${escapeHtml(o.name)}</b><small>${escapeHtml(o.does)}</small></div>
            <div class="sd-offer">${this.cost(o.offer)}${this.button(t(locale, 'planet.build'), o.offer.command, o.offer.reason, true)}</div>
          </li>`,
        )
        .join('');
      return {
        title: t(locale, 'planet.freeSlot', {
          layer: t(locale, `planet.layer.${pop.layer}`).toLowerCase(),
        }),
        sub: '',
        body: `<p class="sd-label">${t(locale, 'planet.buildHint')}</p><ul class="sd-options">${options}</ul>`,
      };
    }
    if (pop.kind !== 'building') return null;
    const picked = view.buildings.find((b) => b.index === pop.index);
    if (!picked) return null;
    return {
      title: picked.name,
      sub: t(locale, 'planet.level', { level: picked.level }),
      body: `<div class="sd-muted">${escapeHtml(picked.does)}</div>
        ${
          picked.upgrade
            ? `<div class="sd-offer">${this.cost(picked.upgrade)}${this.button(
                t(locale, 'planet.upgrade', { level: picked.level + 1 }),
                picked.upgrade.command,
                picked.upgrade.reason,
                true,
              )}</div>`
            : `<div class="sd-label">${picked.building ? t(locale, 'planet.building', { level: picked.building.level, left: picked.building.left }) : t(locale, 'planet.maxed')}</div>`
        }`,
    };
  }

  // ---------- market ----------

  private market(state: GameState, view: MarketView | null): string {
    if (!view) return '';
    const { locale } = this.viewer;
    const notices = view.notices
      .map(
        (n) =>
          `<div class="sd-notice"><b>${escapeHtml(n.text)}</b><small>${escapeHtml(n.note)}</small></div>`,
      )
      .join('');
    const rows = view.goods
      .map((g) => {
        const cls = g.change > 0.005 ? 'sd-up' : g.change < -0.005 ? 'sd-down' : 'sd-muted';
        const tags = [
          g.cheap ? `<span class="sd-badge sd-badge--you">${t(locale, 'market.cheap')}</span>` : '',
          g.embargoed
            ? `<span class="sd-badge sd-badge--alert">${t(locale, 'market.embargoed')}</span>`
            : '',
          g.warning ? `<span class="sd-badge sd-badge--heat">${escapeHtml(g.warning)}</span>` : '',
        ].join('');
        return `<tr class="sd-row ${g.good === this.good ? 'sd-row--picked' : ''}" data-good="${g.good}">
          <td><span class="sd-good">${goodIcon(g.good)}${escapeHtml(g.name)}</span>${tags ? `<div class="sd-tags">${tags}</div>` : ''}</td>
          <td><b>${num(locale, g.price, 1)}</b></td>
          <td class="${cls}">${g.change > 0 ? '+' : ''}${num(locale, g.change * 100)}%</td>
          <td class="${cls}">${spark(g.history)}</td>
          <td>${num(locale, g.stock)}</td>
        </tr>`;
      })
      .join('');
    const name = t(locale, `resource.${this.good}`);
    const trades = (['buy', 'sell'] as const)
      .map((side) =>
        ORDER_SIZES.map((n) => {
          const d = deal(state, this.viewer, this.good, side, n);
          const label = t(locale, side === 'buy' ? 'market.buy' : 'market.sell', { n });
          const sub = d.command
            ? t(locale, 'market.dealCost', {
                total: num(locale, d.total),
                unit: num(locale, d.perUnit, 2),
              })
            : (d.reason ?? '');
          return `<button class="sd-btn sd-btn--deal ${side === 'buy' ? 'sd-btn--primary' : ''}" type="button" ${d.command ? `data-command="${cmd(d.command)}"` : 'disabled'} title="${escapeHtml(sub)}"><span>${label}</span><small>${escapeHtml(sub)}</small></button>`;
        }).join(''),
      )
      .join('');
    const c = view.currency;
    const emissions = c.emissions
      .map(
        (e) =>
          `<button class="sd-btn sd-btn--deal" type="button" data-command="${cmd(e.command)}"><span>${t(locale, 'market.currency.print', { amount: num(locale, e.amount) })}</span><small>${t(locale, 'market.currency.printNote', { rate: num(locale, e.rateAfter, 2) })}</small></button>`,
      )
      .join('');
    const rates = view.rates
      .map(
        (r) =>
          `<span class="sd-rate"><span class="sd-emblem sd-emblem--sm" style="--seat:var(--seat-${r.id.slice(1)})"></span>${escapeHtml(r.name)} <b>${num(locale, r.rate, 2)}</b></span>`,
      )
      .join('');
    const contracts = view.contracts.length
      ? `<ul class="sd-contracts">${view.contracts
          .map(
            (k) => `<li class="${k.alert ? 'sd-contract--alert' : ''}">
              <div><b>${escapeHtml(k.partner)}</b> · ${escapeHtml(k.text)}</div>
              <div class="sd-contract__foot"><span class="sd-label">${escapeHtml(k.status)}</span><span class="sd-buttons">${k.actions
                .map(
                  (a) =>
                    `<button class="sd-btn ${a.primary ? 'sd-btn--primary' : ''}" type="button" data-command="${cmd(a.command)}">${escapeHtml(a.label)}</button>`,
                )
                .join('')}</span></div>
            </li>`,
          )
          .join('')}</ul>`
      : `<p class="sd-label">${t(locale, view.slots.total ? 'market.contracts.empty' : 'market.contracts.none')}</p>`;
    const form =
      view.slots.total > view.slots.used && view.partners.length
        ? this.contractForm(state, view)
        : '';
    const levers = view.levers
      .map((l) => {
        const actions = l.targets.length
          ? `<span class="sd-label">${t(locale, 'market.lever.against')}</span>${l.targets
              .map(
                (x) =>
                  `<button class="sd-btn" type="button" data-command="${cmd(x.command)}">${escapeHtml(x.name)}</button>`,
              )
              .join('')}`
          : l.command
            ? `<button class="sd-btn" type="button" data-command="${cmd(l.command)}">${t(locale, 'market.lever.use')}</button>`
            : l.id === 'dumping' && !l.reason && !l.active
              ? `<button class="sd-btn" type="button" data-command="${cmd({ type: 'useLever', playerId: this.viewer.me, lever: 'dumping', good: this.good, quantity: 50 })}">${t(locale, 'market.lever.dump', { good: name.toLowerCase() })}</button>`
              : '';
        return `<div class="sd-panel sd-lever ${l.active ? 'sd-lever--on' : ''} ${l.locked ? 'sd-lever--locked' : ''}"><b>${leverReady(l) ? canDot : ''}${escapeHtml(l.name)}</b><small>${escapeHtml(l.desc)}</small>${(l.id === 'dumping' ? dumpingImpact(state, this.viewer, this.good) : l.impact).map((x) => `<small class="sd-lever__impact">${escapeHtml(x)}</small>`).join('')}<small>${escapeHtml(l.note)}</small>${l.locked ? `<small class="sd-lever__lock">${escapeHtml(l.locked)}</small>` : ''}${actions ? `<div class="sd-buttons">${actions}</div>` : ''}</div>`;
      })
      .join('');
    // Five sub-tabs (#152): each fits the panel without scrolling.
    const counts = this.counts?.market;
    this.marketTabs = `${notices}${subTabs(
      MARKET_SUBS.map((id) => ({
        id,
        label: t(locale, `market.sub.${id}`),
        count:
          id === 'levers'
            ? (counts?.levers ?? 0)
            : id === 'contracts'
              ? (counts?.contracts ?? 0)
              : 0,
      })),
      this.marketSub,
    )}`;
    switch (this.marketSub) {
      case 'prices':
        return `
      <section class="sd-section">
        <div class="sd-section__head"><span class="sd-label">${t(locale, 'market.pricesNote')}</span></div>
        <table class="sd-table sd-table--pick"><thead><tr><th>${t(locale, 'market.col.good')}</th><th>${t(locale, 'market.col.price')}</th><th colspan="2">${t(locale, 'market.col.change')}</th><th>${t(locale, 'market.col.stock')}</th></tr></thead><tbody>${rows}</tbody></table>
        <div class="sd-trade">
          <div class="sd-section__head"><h3>${t(locale, 'market.trade', { good: escapeHtml(name) })}</h3><span class="sd-label">${t(locale, 'market.dealNote')}</span></div>
          <div class="sd-deals">${trades}</div>
        </div>
      </section>`;
      case 'currency':
        return `
      <section class="sd-section">
        <div class="sd-section__head"><span class="sd-label">${t(locale, 'market.currency.rate', { rate: num(locale, c.rate, 2) })} · ${t(locale, 'market.currency.fee', { fee: c.fee })}</span></div>
        <div class="sd-label">${t(locale, 'market.currency.reserve', { name: escapeHtml(c.issuer) })}</div>
        <div class="sd-rates">${rates}</div>
        <div class="sd-deals sd-deals--three">${emissions}</div>
        <p class="sd-label">${t(locale, 'market.currency.printHint')}</p>
      </section>`;
      case 'contracts':
        return `
      <section class="sd-section">
        <div class="sd-section__head"><span class="sd-label">${t(locale, 'market.contracts.slots', view.slots)}</span></div>
        ${contracts}${form}
      </section>`;
      case 'auto':
        return `
      <section class="sd-section">
        <p class="sd-label">${t(locale, 'market.auto.hint')}</p>
        ${
          view.autoTrade.locked
            ? `<p class="sd-lever__lock">${escapeHtml(view.autoTrade.locked)}</p>`
            : `${
                view.autoTrade.sellAll.length
                  ? `<button class="sd-btn sd-btn--primary sd-btn--small" type="button" data-commands="${escapeHtml(JSON.stringify(view.autoTrade.sellAll))}">${t(locale, 'market.auto.sellAll', { n: view.autoTrade.sellAll.length })}</button>`
                  : ''
              }<table class="sd-table sd-auto"><tbody>${view.autoTrade.rows
                .map(
                  (r) => `<tr>
                    <td><span class="sd-good">${goodIcon(r.good)}${escapeHtml(r.name)}</span><small class="sd-muted">${escapeHtml(r.note)}</small></td>
                    <td><span class="sd-segmented">${(['off', 'sell', 'buy'] as const)
                      .map(
                        (m) =>
                          `<button class="sd-btn sd-btn--small" type="button" aria-pressed="${r.mode === m}" data-command="${cmd(r.set[m])}">${t(locale, `market.auto.${m}`)}</button>`,
                      )
                      .join('')}</span></td>
                  </tr>`,
                )
                .join('')}</tbody></table>`
        }
      </section>`;
      case 'levers':
        return `
      <section class="sd-section">
        <div class="sd-section__head"><span class="sd-label">${t(locale, 'market.levers.influence', { n: num(locale, view.influence) })}</span></div>
        <div class="sd-levers">${levers}</div>
      </section>`;
    }
  }

  private contractForm(state: GameState, view: MarketView): string {
    const { locale } = this.viewer;
    const f = this.form;
    if (!view.partners.some((p) => p.id === f.other)) f.other = view.partners[0]!.id;
    if (!f.price) f.price = Math.round(state.market[f.good].price * 10) / 10;
    const select = (name: string, options: [string, string][], value: string) =>
      `<select class="sd-select" name="${name}">${options.map(([v, l]) => `<option value="${v}" ${v === value ? 'selected' : ''}>${escapeHtml(l)}</option>`).join('')}</select>`;
    const field = (label: string, control: string) =>
      `<label class="sd-field"><span class="sd-label">${label}</span>${control}</label>`;
    return `<form class="sd-form" data-form="contract">
      <h3>${t(locale, 'market.propose')}</h3>
      <div class="sd-form__grid">
        ${field(
          t(locale, 'market.propose.partner'),
          select(
            'other',
            view.partners.map((p) => [p.id, p.name]),
            f.other,
          ),
        )}
        ${field(
          t(locale, 'market.propose.role'),
          select(
            'role',
            [
              ['sell', t(locale, 'market.propose.sell')],
              ['buy', t(locale, 'market.propose.buy')],
            ],
            f.role,
          ),
        )}
        ${field(
          t(locale, 'market.propose.good'),
          select(
            'good',
            TRADE_GOOD_IDS.map((g) => [g, t(locale, `resource.${g}`)]),
            f.good,
          ),
        )}
        ${field(t(locale, 'market.propose.quantity'), `<input class="sd-input" type="number" name="quantity" min="1" max="${MARKET.maxOrderUnits}" value="${f.quantity}" />`)}
        ${field(t(locale, 'market.propose.price'), `<input class="sd-input" type="number" name="price" min="0.1" step="0.1" value="${f.price}" />`)}
        ${field(t(locale, 'market.propose.deliveries'), `<input class="sd-input" type="number" name="deliveries" min="1" max="20" value="${f.deliveries}" />`)}
      </div>
      <button class="sd-btn sd-btn--primary" type="button" data-action="propose">${t(locale, 'market.propose.send')}</button>
    </form>`;
  }

  // ---------- input ----------

  private button(
    label: string,
    command: Command | null,
    reason: string | null,
    primary: boolean,
  ): string {
    return `<span class="sd-offer__action">${
      command
        ? `<button class="sd-btn ${primary ? 'sd-btn--primary' : ''}" type="button" data-command="${cmd(command)}">${escapeHtml(label)}</button>`
        : `<button class="sd-btn" type="button" disabled>${escapeHtml(label)}</button>`
    }${reason ? `<small class="sd-offer__why">${escapeHtml(reason)}</small>` : ''}</span>`;
  }

  private click(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target.closest('.sd-toggle')) return this.toggle();
    const tab = target.closest<HTMLElement>('[data-tab]')?.dataset.tab as TabId | undefined;
    if (tab) {
      event.preventDefault();
      return this.show(tab);
    }
    if (target.closest('[data-action="close-pop"]')) return this.closePop();
    const command = target.closest<HTMLElement>('[data-command]')?.dataset.command;
    if (command) {
      this.order(JSON.parse(command) as Command);
      // A slot taken, a building or a technology begun: its card has done its work.
      if (target.closest('.sd-pop') && this.pop && this.pop.kind !== 'ship') this.closePop();
      return;
    }
    // Several orders at once (#123: every rule in one click).
    const commands = target.closest<HTMLElement>('[data-commands]')?.dataset.commands;
    if (commands) {
      for (const c of JSON.parse(commands) as Command[]) this.order(c);
      return;
    }
    const sub = target.closest<HTMLElement>('[data-sub]')?.dataset.sub;
    if (sub) {
      if (this.tab === 'market') this.marketSub = sub as MarketSub;
      if (this.tab === 'fleet') this.fleetSub = sub as FleetSub;
      this.pop = null;
      return this.redraw(true);
    }
    const focus = target.closest<HTMLElement>('[data-focus]')?.dataset.focus;
    if (focus !== undefined) return this.onFocusFleet(Number(focus));
    const opener = target.closest<HTMLElement>(
      '[data-tech], [data-building], [data-slot], [data-ship]',
    );
    if (opener && !opener.closest('.sd-pop')) {
      const d = opener.dataset;
      const pop: Pop = d.tech
        ? { kind: 'tech', id: d.tech as TechId }
        : d.building !== undefined
          ? { kind: 'building', index: Number(d.building) }
          : d.slot
            ? { kind: 'slot', layer: d.slot as SlotLayer }
            : { kind: 'ship', id: d.ship! };
      // The same thing again closes its card.
      this.pop = JSON.stringify(pop) === JSON.stringify(this.pop) ? null : pop;
      this.popTop = this.besideTop(opener);
      return this.redraw(true);
    }
    const good = target.closest<HTMLElement>('tr[data-good]')?.dataset.good;
    if (good) {
      this.good = good as TradeGoodId;
      return this.redraw(true);
    }
    if (target.closest('[data-action="propose"]')) {
      this.order(proposal(this.viewer, this.form));
      this.redraw(true);
    }
  }

  /** The card's top beside `opener`, kept inside the panel's height. */
  private besideTop(opener: HTMLElement): number {
    const box = this.el.getBoundingClientRect();
    const at = opener.getBoundingClientRect().top - box.top - 8;
    return Math.max(0, Math.min(at, box.height - 440));
  }

  private change(event: Event) {
    const field = event.target as HTMLInputElement | HTMLSelectElement;
    if (!field.closest('[data-form="contract"]')) return;
    const f = this.form;
    switch (field.name) {
      case 'other':
        f.other = field.value;
        break;
      case 'role':
        f.role = field.value as 'sell' | 'buy';
        break;
      case 'good':
        f.good = field.value as TradeGoodId;
        // A new good starts from its exchange price.
        f.price = this.state ? Math.round(this.state.market[f.good].price * 10) / 10 : f.price;
        break;
      case 'quantity':
        f.quantity = Number(field.value) || f.quantity;
        break;
      case 'price':
        f.price = Number(field.value) || f.price;
        break;
      case 'deliveries':
        f.deliveries = Number(field.value) || f.deliveries;
        break;
    }
    this.redraw(true);
  }

  dispose() {
    this.el.remove();
  }
}

/** A price line over the last cycles, coloured like the change beside it. */
function spark(values: readonly number[]): string {
  if (values.length < 2) return '';
  const w = 64;
  const h = 20;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const points = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * w;
      const y = h - 2 - ((v - min) / (max - min || 1)) * (h - 4);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return `<svg class="sd-spark" viewBox="0 0 ${w} ${h}" aria-hidden="true"><polyline points="${points}" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>`;
}
